import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Package } from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { token } = useAuth();
  const { clearCart } = useCart();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [orderDetails, setOrderDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        clearCart();
        // Optionally fetch order details here
      } else if (response.data.status === 'expired') {
        setPaymentStatus('failed');
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Navbar />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        {paymentStatus === 'checking' && (
          <div className="text-center" data-testid="payment-checking">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-black mx-auto mb-6"></div>
            <h1 className="font-heading text-3xl font-bold mb-4">Processing your payment...</h1>
            <p className="text-gray-600">Please wait while we confirm your order</p>
          </div>
        )}

        {paymentStatus === 'success' && (
          <div className="text-center" data-testid="payment-success">
            <div className="bg-green-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="font-heading text-4xl font-bold mb-4">Order Confirmed!</h1>
            <p className="text-xl text-gray-600 mb-8">
              Thank you for your purchase. Your order has been successfully placed.
            </p>

            <div className="bg-gray-50 p-8 mb-8 text-left">
              <h2 className="font-heading text-xl font-bold mb-4 flex items-center">
                <Package className="mr-2 h-5 w-5" />
                Maksura Shipping Information
              </h2>
              <div className="space-y-3 text-sm">
                <p><strong>Origin:</strong> Guangzhou, China</p>
                <p><strong>Delivery:</strong> Fast shipping - you'll receive tracking updates via email</p>
                <p><strong>Tracking:</strong> You will receive email updates as your package progresses</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/dashboard">
                <Button className="bg-black text-white hover:bg-zinc-800 py-4 px-8 uppercase tracking-widest text-xs font-bold" data-testid="view-orders-button">
                  View My Orders
                </Button>
              </Link>
              <Link to="/products">
                <Button variant="outline" className="border border-gray-200 hover:border-black py-4 px-8 uppercase tracking-widest text-xs font-bold">
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </div>
        )}

        {(paymentStatus === 'failed' || paymentStatus === 'error' || paymentStatus === 'timeout') && (
          <div className="text-center" data-testid="payment-failed">
            <div className="bg-red-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">✕</span>
            </div>
            <h1 className="font-heading text-4xl font-bold mb-4">Payment {paymentStatus === 'timeout' ? 'Timeout' : 'Failed'}</h1>
            <p className="text-xl text-gray-600 mb-8">
              {paymentStatus === 'timeout' 
                ? 'We could not confirm your payment. Please check your email for confirmation or contact support.'
                : 'There was an issue processing your payment. Please try again.'}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/checkout">
                <Button className="bg-black text-white hover:bg-zinc-800 py-4 px-8 uppercase tracking-widest text-xs font-bold">
                  Try Again
                </Button>
              </Link>
              <Link to="/cart">
                <Button variant="outline" className="border border-gray-200 hover:border-black py-4 px-8 uppercase tracking-widest text-xs font-bold">
                  Back to Cart
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderSuccessPage;
